import { Link } from "react-router-dom";
import { ArrowRight } from "@/components/Icon";
import techZeefdruk from "@/assets/techniques/zeefdruk.webp";
import techTransfer from "@/assets/techniques/transferdruk.webp";
import techBorduren from "@/assets/techniques/borduren.webp";
import techDtf from "@/assets/techniques/dtf.webp";

export type TechniqueKey = "zeefdruk" | "transferdruk" | "borduren" | "dtf";

const techniques: Record<TechniqueKey, {
  title: string;
  description: string;
  link: string;
  label: string;
  image: string;
}> = {
  zeefdruk: {
    title: "Zeefdruk",
    description: "Zeefdruk is een betrouwbare methode met een hoge kleurechtheid. De te drukken print wordt met een vrij dikke laag inkt overgebracht op het textiel, waardoor de bedrukking goed dekkend is — geschikt voor iedere kleur textiel.",
    link: "/druktechnieken/zeefdrukkerij",
    label: "Ideaal bij grotere oplages",
    image: techZeefdruk,
  },
  transferdruk: {
    title: "Transferdruk",
    description: "Transferdruk leent zich uitstekend voor full color bedrukkingen. De kleuren worden via een folie helder en haarscherp overgebracht op het textiel — perfect voor foto's en personalisaties.",
    link: "/druktechnieken/transferdruk",
    label: "Full color",
    image: techDtf,
  },
  borduren: {
    title: "Borduren",
    description: "Borduren biedt veel mogelijkheden — vrijwel ieder product kan geborduurd worden, van t-shirts en jassen tot sweaters, hoodies en caps. Betaalbaar en met een professionele uitstraling, met tot wel 15 kleuren per product.",
    link: "/druktechnieken/borduren",
    label: "Vanaf 25 stuks",
    image: techBorduren,
  },
  dtf: {
    title: "DTF Printing",
    description: "Met DTF (Direct To Film) Printing wordt de afbeelding eerst full color op een transparante film geprint en daarna met een hittepers op het textiel geperst. Milieuvriendelijk, wasbaar tot 40°C en al leverbaar vanaf 10 stuks.",
    link: "/druktechnieken/dtf-printing",
    label: "Vanaf 10 stuks",
    image: techTransfer,
  },
};

interface TechniqueCardsProps {
  items?: TechniqueKey[];
  /** Optional per-page description overrides; defaults to the standard texts. */
  descriptions?: Partial<Record<TechniqueKey, string>>;
}

const TechniqueCards = ({ items = ["zeefdruk", "transferdruk", "borduren", "dtf"], descriptions }: TechniqueCardsProps) => (
  <div className={`grid grid-cols-1 gap-6 md:grid-cols-2 ${items.length === 3 ? "lg:grid-cols-3" : "lg:grid-cols-4"}`}>
    {items.map((key) => {
      const technique = techniques[key];
      return (
        <Link
          key={key}
          to={technique.link}
          className="group flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-card transition-all hover:border-primary hover:shadow-xl"
        >
          <div className="relative aspect-[4/3] overflow-hidden">
            <img
              src={technique.image}
              alt={`${technique.title} bij Magic Print`}
              loading="lazy"
              width={800}
              height={600}
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          </div>
          <div className="flex flex-1 flex-col p-6">
            <h3 className="mb-2 text-xl font-bold text-foreground transition-colors group-hover:text-primary">
              {technique.title}
            </h3>
            <p className="mb-4 flex-1 leading-relaxed text-muted-foreground">{descriptions?.[key] ?? technique.description}</p>
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm font-medium text-primary">{technique.label}</span>
              <span className="flex items-center gap-1 text-sm text-primary">
                Lees meer <ArrowRight className="h-4 w-4" />
              </span>
            </div>
          </div>
        </Link>
      );
    })}
  </div>
);

export default TechniqueCards;